import { FC } from 'react';

import { AnnotationsOverlay } from '@/shared/graphics/RenderComponents/AnnotationsOverlay';

import { MedicalImage, type MedicalImageProps } from '@/features/medicalImage';

import type {
  FullHeadMprSliceGroupView,
  FullHeadMprSliceRecordView,
  FullHeadMprSliceTileView,
} from '../../config';
import {
  makeTileMedicalImage,
  makeFullHeadMprDisplayAnnotations,
} from '../../lib';

type FullHeadMprSliceImageProps = Omit<
  MedicalImageProps,
  'medicalImage' | 'imageOverlay' | 'selfPaintSrc'
> & {
  src: string;
  tile: FullHeadMprSliceTileView;
  group?: FullHeadMprSliceGroupView;
  record?: FullHeadMprSliceRecordView;
};

export const FullHeadMprSliceImage: FC<FullHeadMprSliceImageProps> = (
  props,
) => {
  const { group, src, tile, record, ...medicalImageProps } = props;
  const displayView =
    tile.displayView ?? (tile.kind === 'view' ? tile.view : null);
  const displayWidthPx = record?.geometry?.widthPx ?? 0;
  const displayHeightPx = displayView
    ? (displayWidthPx * displayView.heightMm) / displayView.widthMm
    : record?.geometry?.heightPx ?? 0;

  return (
    <MedicalImage
      {...medicalImageProps}
      medicalImage={makeTileMedicalImage({ group, tile })}
      selfPaintSrc={src}
      imageOverlay={
        record?.hasAnnotations && record.geometry ? (
          <AnnotationsOverlay
            annotations={
              displayView
                ? makeFullHeadMprDisplayAnnotations({
                    record,
                    to: {
                      view: displayView,
                      widthPx: displayWidthPx,
                      heightPx: displayHeightPx,
                    },
                  })
                : record.annotations
            }
            heightPx={displayHeightPx}
            scaleMmPerPixel={
              displayView
                ? displayView.widthMm / displayWidthPx
                : record.geometry.scaleMmPerPx
            }
            widthPx={displayWidthPx}
          />
        ) : null
      }
    />
  );
};
